/**
 * Mic Button (Split Button)
 *
 * Two-part control for voice input:
 * - Chevron: opens popup to switch mode (with descriptions)
 * - Mic icon: immediately starts/stops the last-used mode (one click)
 *
 * This preserves quick-start behavior while adding discoverable mode switching.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Radio, Circle, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';

export type MicMode = 'transcribe' | 'record';

interface MicButtonProps {
  mode: MicMode;
  isActive: boolean;
  isRecording: boolean;
  transcribeSupported: boolean;
  recordSupported: boolean;
  onSelectAndStart: (mode: MicMode) => void;
  onStop: () => void;
}

const MODES = [
  {
    value: 'transcribe' as const,
    label: 'Live Transcription',
    description: 'Words appear in the textarea as you speak. Ideal for quick dictation.',
    browser: 'Chrome, Edge, Safari',
    icon: Radio,
  },
  {
    value: 'record' as const,
    label: 'Record Audio',
    description: 'Records an audio file sent to the server for Whisper STT processing.',
    browser: 'All modern browsers',
    icon: Circle,
  },
];

export function MicButton({
  mode,
  isActive,
  isRecording,
  transcribeSupported,
  recordSupported,
  onSelectAndStart,
  onStop,
}: MicButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Mic icon click: start last-used mode or stop
  const handleMicClick = useCallback(() => {
    if (isActive) {
      onStop();
    } else {
      onSelectAndStart(mode);
    }
  }, [isActive, mode, onSelectAndStart, onStop]);

  // Popup option click: switch mode and start
  const handleSelect = useCallback(
    (selectedMode: MicMode) => {
      setIsOpen(false);
      onSelectAndStart(selectedMode);
    },
    [onSelectAndStart],
  );

  const isSupported = (modeValue: MicMode) =>
    modeValue === 'transcribe' ? transcribeSupported : recordSupported;

  const currentModeSupported = isSupported(mode);

  return (
    <div className="relative flex items-center" ref={ref}>
      {/* Chevron: opens mode popup */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          'p-1 rounded-l-md transition-colors border-r border-border/50',
          isOpen
            ? 'bg-bg-hover text-text-primary'
            : 'text-text-muted/50 hover:text-text-muted hover:bg-bg-hover',
        )}
        title="Voice input mode"
      >
        <ChevronDown className="w-2.5 h-2.5" />
      </button>

      {/* Mic icon: start/stop immediately */}
      <button
        type="button"
        onClick={handleMicClick}
        disabled={!currentModeSupported}
        className={cn(
          'p-1.5 rounded-r-md transition-colors',
          isActive
            ? 'bg-red-500/20 text-red-400 animate-pulse'
            : currentModeSupported
              ? 'text-text-muted hover:text-accent-orange hover:bg-bg-hover'
              : 'text-text-muted/30 cursor-not-allowed',
        )}
        title={
          !currentModeSupported
            ? 'Not supported in this browser'
            : isActive
              ? 'Stop'
              : mode === 'transcribe'
                ? 'Start voice input'
                : 'Start recording'
        }
      >
        {isActive && isRecording ? (
          <Square className="w-3.5 h-3.5" />
        ) : (
          <Mic className="w-3.5 h-3.5" />
        )}
      </button>

      {/* Mode selection popup */}
      {isOpen && (
        <div className="absolute bottom-full right-0 mb-1.5 w-72 bg-bg-secondary border border-border rounded-lg shadow-lg shadow-black/30 z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border">
            <span className="text-[10px] text-text-muted uppercase tracking-wider font-medium">
              Voice Input Mode
            </span>
          </div>
          {MODES.map((m) => {
            const supported = isSupported(m.value);
            const current = m.value === mode;
            const Icon = m.icon;
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => supported && handleSelect(m.value)}
                disabled={!supported}
                className={cn(
                  'w-full text-left px-3 py-3 transition-colors border-b border-border last:border-b-0',
                  !supported
                    ? 'opacity-40 cursor-not-allowed'
                    : current
                      ? 'bg-accent-orange/10 hover:bg-accent-orange/15'
                      : 'hover:bg-bg-hover',
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    className={cn(
                      'w-3.5 h-3.5 shrink-0',
                      current ? 'text-accent-orange' : 'text-text-muted',
                    )}
                  />
                  <span
                    className={cn(
                      'text-xs font-medium',
                      current ? 'text-accent-orange' : 'text-text-primary',
                    )}
                  >
                    {m.label}
                  </span>
                  {current && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-sm bg-accent-orange/20 text-accent-orange ml-auto">
                      active
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-text-muted mt-1 ml-[22px] leading-relaxed">
                  {m.description}
                </p>
                {!supported && (
                  <p className="text-[10px] text-accent-red mt-0.5 ml-[22px]">
                    Not supported in this browser
                  </p>
                )}
                {supported && (
                  <p className="text-[10px] text-text-muted/50 mt-0.5 ml-[22px]">
                    {m.browser}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
